import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import HubertPopoverFieldEdit from 'components/HubertUI/HubertPopoverFieldEdit';
import { useInTemplateEditAndUneditable } from 'hooks/useInTemplateEditAndUneditable';
import { CircleMinus } from 'lucide-react';
import type { ComponentProps } from 'react';
import { editReply, editReplyOptions, removeReply, removeReplyOptions } from 'rtk/Question.Editor.Slice';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { selectQuestionLanguage, selectQuestionMode } from 'rtk/questionEditorSelector';
import { toast } from 'sonner';
import { ECreateQuestionMode, useQuestionEditorContext } from '../context/create-question.context';
import { updateInTemplateReplyOptions } from 'rtk/questionEditorThunks';
import { useTemplateId } from 'hooks/useTemplate';
import { EUtter, stripHTML } from '@hubertai/screening-question-types';

interface Props extends ComponentProps<'div'> {
  reply: string;
  replies: string[];
  field: 'options' | 'intents';
  fieldIndex: number;
  itemIndex: number;
  optionId?: string;
}

const Reply = ({ reply, replies, field, fieldIndex, itemIndex, optionId, ...props }: Props) => {
  const { t } = useTranslation('Questionsv2');
  const { questionId } = useQuestionEditorContext();
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const inTemplateEditAndUneditable = useInTemplateEditAndUneditable(questionId, mode);
  const errors = useAppSelector((state) => state.questionEditor.byId[questionId].optionsErrors);
  const dispatch = useAppDispatch();
  const templateId = useTemplateId();

  if (errors?.length) errors.map((x) => toast.error(x));
  const update = (value: string) => {
    let s = value;
    if (s.endsWith('<p><br></p>')) {
      s = s.substring(0, s.length - 11);
    }

    // Guarding values in EUtter from looking like:
    // <p class="" style=""> utter_confirm </p>
    if (Object.values(EUtter).includes(stripHTML(s)?.trim() as EUtter)) {
      s = stripHTML(s)?.trim() || '';
    }

    //@ts-ignore
    if (!stripHTML(s).replace(/\s/g, '').length) {
      toast.info(t('reply.toast.emptyMessages'));
    }

    if (field === 'intents')
      dispatch(editReply({ questionId, fieldIndex, itemIndex, newReply: s, language: questionLanguage }));
    else {
      if (inTemplateEditAndUneditable) {
        updateInTemplateReplyOptions({
          templateId,
          questionId,
          optionId,
          replies,
          operation: 'edit',
          itemIndex,
          newReply: s,
          language: questionLanguage,
          dispatch,
          mode,
        });
      } else {
        dispatch(editReplyOptions({ questionId, fieldIndex, itemIndex, newReply: s, language: questionLanguage }));
      }
    }
  };

  return (
    <div
      {...props}
      className={clsx(
        'flex flex-row justify-between gap-4',
        'hubert-vertical-center',
        'px-4 py-4',
        'hover:bg-neutral-purple',
        'rounded-md',
      )}
    >
      <HubertPopoverFieldEdit>
        <HubertPopoverFieldEdit.Trigger tooltipContent={t('reply.editLabelTooltip')} containerClass="w-full" asChild>
          <div
            className={clsx(
              'text-sm',
              'text-wrap',
              'cursor-pointer',
              'border border-gray-200',
              'p-3',
              'rounded-md',
              mode === ECreateQuestionMode.InTemplateEdit ? '' : 'cursor-pointer',
            )}
            dangerouslySetInnerHTML={{
              __html: reply && stripHTML(reply)?.replace(/\s/g, '').length ? reply : t('reply.clickToEdit'),
            }}
          />
        </HubertPopoverFieldEdit.Trigger>
        <HubertPopoverFieldEdit.Content
          editType="rich-text"
          value={reply}
          onFieldCommit={(value) => {
            update(value);
          }}
        >
          <div>
            <div className="text-lg font-semibold">{t('reply.editHeading')}</div>
          </div>
        </HubertPopoverFieldEdit.Content>
      </HubertPopoverFieldEdit>

      <div className={clsx('flex flex-row justify-between gap-4')}>
        <CircleMinus
          className={clsx(field === 'intents' && inTemplateEditAndUneditable && 'hidden')}
          onClick={() => {
            if (field === 'intents')
              dispatch(removeReply({ questionId, fieldIndex, itemIndex, language: questionLanguage }));
            else if (inTemplateEditAndUneditable) {
              updateInTemplateReplyOptions({
                templateId,
                questionId,
                optionId,
                replies,
                operation: 'remove',
                itemIndex,
                language: questionLanguage,
                dispatch,
                mode,
              });
            } else {
              dispatch(removeReplyOptions({ questionId, fieldIndex, itemIndex, language: questionLanguage }));
            }
          }}
        />
      </div>
    </div>
  );
};

export default Reply;
